import { Link } from "@tanstack/react-router";
import type { PublicTeam } from "@/lib/types";
import {
  LiveStatusPill,
  type LiveStatusPillState,
} from "@/features/live/components/LiveStatusPill";

/**
 * `TeamCard` — public-grid card for `/teams` (M12a).
 *
 * Brand kit consumer: every color/spacing routes through tokens
 * (`bg-surface`, `border-rule`, `text-ink`, etc.) so the card
 * inherits light + dark theme parity from `tokens.css`.
 *
 * The card surfaces the slim public-team payload: name, slug,
 * match count, and the latest played match date when one
 * exists. Per-team logos / brand colors deferred (Team schema
 * doesn't carry those columns yet). M12c adds a live pill driven
 * by the aggregate `/api/live/team-statuses` poll on the parent
 * page; the card stays a single Link so the whole surface is the
 * tap target — when live, the card already routes to the team
 * page where the "Watch live" CTA takes over.
 */
export function TeamCard({
  team,
  liveState,
}: {
  team: PublicTeam;
  liveState?: LiveStatusPillState;
}) {
  return (
    <Link
      to="/teams/$teamSlug"
      params={{ teamSlug: team.slug }}
      data-testid="team-card"
      data-team-slug={team.slug}
      className="group flex flex-col gap-3 rounded border border-rule bg-surface p-5 transition-colors hover:border-accent"
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-display text-lg leading-snug tracking-tight text-ink group-hover:text-accent">
          {team.name}
        </h3>
        <div className="flex items-center gap-1.5">
          {liveState ? <LiveStatusPill state={liveState} /> : null}
          <span className="rounded border border-rule px-2 py-0.5 font-mono text-[0.6rem] uppercase tracking-wider text-ink-muted">
            {team.game_format}
          </span>
        </div>
      </div>
      <dl className="flex flex-wrap gap-x-5 gap-y-1 font-mono text-xs text-ink-muted">
        <div className="flex items-center gap-1.5">
          <dt className="uppercase tracking-wider">Matches</dt>
          <dd
            data-testid="team-card-match-count"
            className="font-display text-ink"
          >
            {team.match_count}
          </dd>
        </div>
        {team.last_match_at ? (
          <div className="flex items-center gap-1.5">
            <dt className="uppercase tracking-wider">Last</dt>
            <dd data-testid="team-card-last-at" className="text-ink">
              {team.last_match_at}
            </dd>
          </div>
        ) : null}
      </dl>
      <span
        aria-hidden="true"
        className="mt-auto font-mono text-[0.6rem] uppercase tracking-mono text-ink-muted group-hover:text-ink"
      >
        open team →
      </span>
    </Link>
  );
}
